import { Suspense, lazy } from "react";
import { Navigate, useRoutes, useLocation } from "react-router-dom";
// layouts
import DashboardLayout from "../layouts/dashboard";

// guards
import GuestGuard from "../guards/GuestGuard";
import AuthGuard from "../guards/AuthGuard";
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from "../config";
// components
import LoadingScreen from "../components/LoadingScreen";

// ----------------------------------------------------------------------

export const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense
      fallback={<LoadingScreen isDashboard={pathname.includes("/dashboard")} />}
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: "auth",
      children: [
        {
          path: "login",
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
      ],
    },
    // Dashboard Routes
    {
      path: "dashboard",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: "app", element: <GeneralApp /> },
        { path: "courier-dashboard", element: <CourierDashboard /> },
        { path: "school-wise-order", element: <SchoolOrder /> },
        { path: "variation-bulk", element: <OrderPromotion /> },
        { path: "global-search", element: <GlobalOrderSearch /> },
        { path: "parent", element: <ParentMaster /> },
        { path: "invoice", element: <InvoiceDetails /> },
        { path: "students-list", element: <StudentMaster /> },
        { path: "tickets", element: <ReportedIssue /> },
        { path: "raised-ticket", element: <RaisedTicket /> },
        { path: "student-sizing", element: <StudentSizing /> },
        { path: "order-status", element: <OrderStatus /> },
        { path: "archival", element: <ArchivalAllData /> },
        { path: "place-order", element: <CreateOrder /> },
        {
          path: "master",
          children: [
            {
              element: <Navigate to="/dashboard/master/category" replace />,
              index: true,
            },
            { path: "category", element: <Category /> },
            { path: "house", element: <House /> },
            { path: "variation", element: <Variation /> },
            { path: "package", element: <PackageMaster /> },
            { path: "shipping", element: <ShippingMaster /> },
            { path: "standard", element: <StandardMaster /> },
            { path: "product", element: <ProductMaster /> },
            { path: "school", element: <SchoolMaster /> },
            { path: "vendor", element: <VendorMaster /> },
            { path: "ticket-category", element: <TicketCategoryMaster /> },
            { path: "promocode", element: <PromocodeMaster /> },
            { path: "publisher", element: <Publisher /> },
            { path: "party", element: <Party /> },
          ],
        },
        {
          path: "inventory",
          children: [
            {
              element: <Navigate to="dashboard/inventory/dashboard" replace />,
              index: true,
            },
            { path: "dashboard", element: <InventoryDashboard /> },
            { path: "draft-order", element: <DraftOrder /> },
            { path: "purchase-order", element: <PurchaseOrder /> },
            { path: "outward-sale", element: <OutWardOrder /> },
            { path: "stock-management", element: <StockManage /> },
          ],
        },
        {
          path: "reports",
          children: [
            {
              element: <Navigate to="/dashboard/reports/new-report" replace />,
              index: true,
            },
            { path: "order-summary", element: <OrderSummary /> },
            { path: "student-sizing-report", element: <StudentSizingReport /> },
            { path: "test-report", element: <TestReport /> },
            { path: "new-report", element: <NewOrder /> },
            {
              path: "new-and-pending-report",
              element: <NewAndPendingReport />,
            },
            {
              path: "student-wise-pending-report",
              element: <StudentWisePendingOrders />,
            },
            {
              path: "school-wise-pending-report",
              element: <SchoolWisePendingOrders />,
            },
            { path: "date-wise-report", element: <OrderedProductReport /> },
            { path: "order-wise-gst-report", element: <OrderWiseGSTReport /> },
            { path: "sale-with-gst", element: <GSTReport type={"withGst"} /> },
            { path: "sale-without-gst-report", element: <GSTReport /> },
            { path: "sales-report", element: <SalesReport /> },
            { path: "settlement-report", element: <SettlementReport /> },
            {
              path: "shipping-wise-report",
              element: <GSTReport type={"shipping"} />,
            },
          ],
        },
        {
          path: "courier",
          children: [
            {
              element: <Navigate to="/dashboard/courier/add-company" replace />,
              index: true,
            },
            { path: "add-company", element: <DeliveryCompany /> },
            { path: "add-rider", element: <DeliveryRider /> },
          ],
        },
      ],
    },

    // Main Routes

    {
      path: "/",
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      ),
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import("../pages/auth/Login")));

// DASHBOARD


// GENERAL
const GeneralApp = Loadable(
  lazy(() => import("../pages/dashboard/GeneralApp"))
);
const CourierDashboard = Loadable(
  lazy(() => import("src/pages/courier-dashboard"))
);
const GlobalOrderSearch = Loadable(
  lazy(() => import("src/pages/global-search"))
);
const CreateOrder = Loadable(lazy(() => import("src/pages/create-order")));
const SchoolOrder = Loadable(lazy(() => import("src/pages/school")));
const OrderPromotion = Loadable(lazy(() => import("src/pages/variation-bulk")));
const RaisedTicket = Loadable(
  lazy(() => import("src/pages/ticket/RaisedTicket"))
);
const InvoiceDetails = Loadable(
  lazy(() => import("src/pages/school/InvoiceDetails"))
);
const ReportedIssue = Loadable(lazy(() => import("src/pages/ticket")));
const OrderStatus = Loadable(lazy(() => import("src/pages/order-status")));
const ArchivalAllData = Loadable(lazy(() => import("src/pages/archival")));
const StudentSizing = Loadable( lazy(() => import("src/pages/student-sizing")) );
  
//Master

const Category = Loadable(lazy(() => import("src/pages/master/catgory")));
const House = Loadable(lazy(() => import("src/pages/master/house")));
const Variation = Loadable(lazy(() => import("src/pages/master/variation")));
const PackageMaster = Loadable(
  lazy(() => import("src/pages/master/package-master"))
);
const TicketCategoryMaster = Loadable(
  lazy(() => import("src/pages/master/ticket-category"))
);
const ShippingMaster = Loadable(
  lazy(() => import("src/pages/master/shipping"))
);
const StandardMaster = Loadable(
  lazy(() => import("src/pages/master/standard"))
);
const SchoolMaster = Loadable(lazy(() => import("src/pages/master/school")));
const VendorMaster = Loadable(lazy(() => import("src/pages/master/vendor")));
const PromocodeMaster = Loadable(
  lazy(() => import("src/pages/master/promocode"))
);
const ProductMaster = Loadable(
  lazy(() => import("src/pages/master/product-master"))
);
const StudentMaster = Loadable(lazy(() => import("src/pages/master/students")));
const ParentMaster = Loadable(
  lazy(() => import("src/pages/master/parent-master"))
);
const Publisher = Loadable(lazy(() => import("src/pages/master/publisher")));
const Party = Loadable(lazy(() => import("src/pages/master/party")));


// Courier

const DeliveryRider = Loadable(lazy(() => import("src/pages/courier/rider")));
const DeliveryCompany = Loadable(
  lazy(() => import("src/pages/courier/company"))
);

// Inventory

const DraftOrder = Loadable(
  lazy(() => import("src/pages/inventory/draft-order"))
);
const InventoryDashboard = Loadable(
  lazy(() => import("src/pages/inventory/dashboard"))
);
const PurchaseOrder = Loadable(
  lazy(() => import("src/pages/inventory/purchase-order"))
);
const OutWardOrder = Loadable(
  lazy(() => import("src/pages/inventory/outward-order"))
);
const StockManage = Loadable(
  lazy(() => import("src/pages/inventory/stockManagement"))
);

// Reports

const NewOrder = Loadable(lazy(() => import("src/pages/reports/NewOrder")));
const NewAndPendingReport = Loadable(
  lazy(() => import("src/pages/reports/NewAndPendingReport"))
);
const OrderedProductReport = Loadable(
  lazy(() => import("src/pages/reports/OrderedProductReport"))
);
const OrderWiseGSTReport = Loadable(
  lazy(() => import("src/pages/reports/OrderWiseGSTReport"))
);
const OrderSummary = Loadable(
  lazy(() => import("src/pages/reports/OrderSummary"))
);
const StudentSizingReport = Loadable(
  lazy(() => import("src/pages/reports/StudentSizingReport"))
);
const TestReport = Loadable(lazy(() => import("src/pages/reports/TestReport")));
const GSTReport = Loadable(lazy(() => import("src/pages/reports/GSTReport")));
const SalesReport = Loadable(
  lazy(() => import("src/pages/reports/SalesReport"))
);
const SettlementReport = Loadable(
  lazy(() => import("src/pages/reports/SettlementReport"))
);
const StudentWisePendingOrders = Loadable(
  lazy(() => import("src/pages/reports/StudentWisePendingOrders"))
);
const SchoolWisePendingOrders = Loadable(
  lazy(() => import("src/pages/reports/SchoolwisePendingOrders"))
);

